import { useEffect } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import useSWR from "swr";
import Head from "next/head";

import api, { fetchTenantConfig } from "../../services/api";

import Layout from "../../components/Layout";
import MediaGrid from "../../components/MediaGrid";
import NoContentPlaceholder from "../../components/NoContentPlaceholder";
import { useAuth } from "../../hooks/useAuth";
import { formatInsertables } from "../../util";

export default function Live({ initialData }) {
  const router = useRouter();
  const { tenant, apiLoaded } = useAuth();

  const { data: liveResponse, mutate, error } = useSWR(
    apiLoaded && `watch/live?event_bucket=${tenant.event_bucket}`,
    k => api.get(k).then(r => r.data),
    { initialData }
  );
  useEffect(() => {
    if (apiLoaded) mutate(initialData, false);
  }, [apiLoaded]);

  if (!liveResponse && !error) {
    return <Layout loader />;
  }

  return (
    <Layout>
      <Head>
        <title>Live | {tenant.virtual_festival_display_name}</title>
      </Head>
      <div>
        <div className="text-gray-100 text-center bg-gray-700 p-4 rounded-lg mb-8">
          <div>
            {formatInsertables(tenant.virtual_festival_livestreams_body, {
              link: (url, label) => (
                <a
                  className="underline hover:text-gray-200"
                  href={url}
                  target="_blank"
                >
                  {label}
                </a>
              )
            })}
          </div>
        </div>
        {liveResponse?.livestreams.length ? (
          <MediaGrid
            items={liveResponse.livestreams.map(livestream => ({
              ...livestream,
              virtual_content_type: "livestream"
            }))}
            festivalId={router.query.festivalId}
          />
        ) : (
          <NoContentPlaceholder
            text={
              error ? (
                `Unable to load live streams: ${error.message}`
              ) : (
                <>No upcoming live streams &ndash; check back soon</>
              )
            }
            emoji="🎞️"
          />
        )}
      </div>
    </Layout>
  );
}

export async function getServerSideProps({ query, res, locale }) {
  if (res) {
    res.setHeader("Cache-Control", "s-maxage=1, stale-while-revalidate");
  }

  const tenant = await fetchTenantConfig(query.festivalId);

  let finalProps;
  try {
    const data = await api
      .get(`watch/live?event_bucket=${tenant.event_bucket}`, {
        auth: { username: tenant.api_key }
      })
      .then(r => r.data);
    finalProps = { initialData: data, initialTenant: tenant };
  } catch (e) {
    finalProps = {};
  }
  return {
    props: {
      ...finalProps
    }
  };
}
